/*!
 * GentlePup theme — review-skin.js
 * Copyright © 2026 Murilo Scigliano. All rights reserved.
 * Proprietary. See LICENSE.
 */
/* The review app renders inside a same-origin iframe and paints its own
   ground, which the theme's stylesheets cannot reach. This walks into
   the iframe and installs a style sheet built from the live token values
   on :root, so the widget wears the palette without a copy of any hex
   value existing here — retune tokens.css and the widget follows. */
(() => {
  'use strict';

  if (window.__gpReviewSkin) return;
  window.__gpReviewSkin = true;

  const HOSTS = '.testimonials__app iframe, .reviews-page__app iframe';

  const buildCss = () => {
    const tokens = getComputedStyle(document.documentElement);
    const t = (name) => tokens.getPropertyValue(name).trim();
    const card = t('--cream-100');
    const ink = t('--cocoa');
    const cream = t('--cream');
    const border = t('--border');
    const font = getComputedStyle(document.body).fontFamily;
    return [
      'html, body, .grid, .scm-row, .scm-container, .header-container { background: transparent !important; }',
      `body, .item, .content-full, .container-name { font-family: ${font} !important; color: ${ink} !important; }`,
      /* Cosmetics only. The app sizes its iframe by measuring its own
         rows and containers, so layout overrides in here — resized
         wrappers, display: contents rows, forced grids — make that
         measurement collapse and the iframe clips the wall. Colour,
         radius and type are safe; geometry belongs to the app.

         One verified exception, phones only: the app fixes its card
         width (~172px) regardless of frame width, so a 343px phone
         frame gets one left-aligned column of narrow cards and a wall
         of empty sand beside it. Forcing the card to full width works
         because the app re-measures the item boxes on every layout —
         positions, row height and frame height all recompute (verified
         live at 390px). box-sizing keeps the 4px gutter border inside
         the width rather than overflowing the frame. */
      '@media (max-width: 479px) { .item { width: 100% !important; box-sizing: border-box !important; } }',
      /* The visible gutter: a transparent border shrinks the card inside
         the slot the masonry gives it — borders are part of the box the
         app measures, so spacing this way cannot desync its layout. The
         hairline edge rides an inset shadow at the padding edge. */
      `.item { background: none !important; background-color: ${card} !important; background-clip: padding-box !important; border: 4px solid transparent !important; border-radius: 20px !important; box-shadow: inset 0 0 0 1px ${border} !important; overflow: hidden !important; }`,
      '.wrap-image, .thumb-img, .img-rv, .img-thumbnail { border-radius: 12px !important; overflow: hidden !important; }',
      '.date { opacity: .55 !important; }',
      `.link-product { color: ${ink} !important; font-weight: 700 !important; background: transparent !important; border-top: 1px solid ${border} !important; box-shadow: none !important; }`,
      `[class*="scm-btn"] { background: ${ink} !important; color: ${cream} !important; border: none !important; border-radius: 999px !important; padding: 10px 22px !important; font-weight: 700 !important; }`,
    ].join('\n');
  };

  const dress = (frame) => {
    if (frame.dataset.gpSkinned === '1') return;
    frame.dataset.gpSkinned = '1';
    const inject = () => {
      let doc;
      try {
        doc = frame.contentDocument;
      } catch (error) {
        return; /* a cross-origin frame is not the widget */
      }
      if (!doc || !doc.head || doc.getElementById('gp-review-skin')) return;
      const style = doc.createElement('style');
      style.id = 'gp-review-skin';
      style.textContent = buildCss();
      doc.head.appendChild(style);
      /* If the app laid its masonry before this sheet arrived, the card
         positions predate the phone-width override. A resize makes it
         re-measure; on wider frames the media query matches nothing and
         the re-layout is a no-op. */
      frame.contentWindow.dispatchEvent(new frame.contentWindow.Event('resize'));
    };
    inject();
    /* The app rebuilds its document on load-more and sort; each rebuild
       fires load on the frame and arrives without the injected sheet. */
    frame.addEventListener('load', inject);
  };

  /* Never disconnect: the LAI widget replaces its iframe node on
     pagination/sort/re-mount, and a replacement frame is a fresh node
     that needs dressing again. The per-frame gpSkinned guard makes
     repeat sweeps idempotent, so keeping the observer alive is cheap. */
  const sweep = () => {
    document.querySelectorAll(HOSTS).forEach(dress);
  };

  /* The widget script creates its iframe well after DOMContentLoaded,
     so watch for it rather than polling on a timer. */
  const observer = new MutationObserver(sweep);
  observer.observe(document.documentElement, { childList: true, subtree: true });
  sweep();
})();
